import React from 'react'
import styled from 'styled-components'
import Heading from 'Event/template/Lefty/Dashboard/Sidebar/Heading'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {Publishable} from 'Event/Dashboard/editor/views/Published'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import AddAgendaButton from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/AgendaList/AddAgendaButton'
import {useEditMode} from 'Event/EditModeProvider'
import {useAttendeeVariables} from 'Event'
import Agenda from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItem'
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd'
import StyledText from 'lib/ui/typography/StyledText'
import {useLeftyTemplate} from 'Event/template/Lefty'
import {useToggle} from 'lib/toggle'
import {AgendaListConfig} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaListConfig'
import {FontStyle} from 'lib/ui/typography/FontStyleInput'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import Section from 'Event/template/Lefty/Dashboard/Sidebar/Section'
import {createPositions, HashMap, Ordered, orderedIdsByPosition} from 'lib/list'
import {
  HasOffset,
  useEditSidebarItem,
} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem'
import {HasRules} from 'Event/attendee-rules'
import {HasSchedule} from 'lib/ui/layout/Scheduled'

export const AGENDA_LIST = 'Agenda List'
export type AgendaListProps = Ordered &
  HasRules &
  HasSchedule &
  HasOffset & {
    type: typeof AGENDA_LIST
    title: string
    description?: string
    footer?: string
    descriptionFontStyles?: FontStyle[]
    footerFontStyles?: FontStyle[]
    items: HashMap<AgendaSettings>
    isEnabled: boolean
  }

export const createAgendaList = (): AgendaListProps => ({
  type: AGENDA_LIST,
  title: 'Agenda',
  description: '',
  footer: 'Agenda Time is in YOUR time zone, not ours',
  items: {},
  footerFontStyles: [],
  descriptionFontStyles: [],
  isEnabled: true,
  offsetTop: 0,
  offsetBottom: 0,
})

export type AgendaSettings = Publishable &
  Ordered & {
    startDate: string
    endDate: string | null
    text: string
    link: string | null
  }

export default function AgendaList(props: AgendaListProps) {
  const {
    title,
    items,
    descriptionFontStyles,
    footer,
    description,
    footerFontStyles,
    isEnabled,
    offsetTop,
    offsetBottom,
  } = props
  const template = useLeftyTemplate()
  const {sidebar} = template
  const isEdit = useEditMode()
  const v = useAttendeeVariables()
  const hasAgenda = Object.keys(items).length > 0
  const {flag: listConfigVisible, toggle: toggleListConfig} = useToggle()

  const showing = hasAgenda && isEnabled

  if (!showing && !isEdit) {
    return null
  }

  const HeadingHolder = (props: {children: React.ReactElement}) => {
    if (isEdit) {
      return <div style={{minHeight: 30}}>{props.children}</div>
    }

    return v(title) ? props.children : null
  }

  return (
    <Section offsetTop={offsetTop} offsetBottom={offsetBottom}>
      <EditModeOnly>
        <AgendaListConfig
          list={props}
          showing={listConfigVisible}
          onClose={toggleListConfig}
        />
        <RemoveAgendaListButton />
      </EditModeOnly>
      <Editable onEdit={toggleListConfig}>
        <HeadingHolder>
          <Heading aria-label="agendas">{v(title)}</Heading>
        </HeadingHolder>
      </Editable>
      <StyledText
        Component={Text}
        fontStyles={descriptionFontStyles}
        aria-label="agendas description"
        color={sidebar.textColor ?? ''}
      >
        {v(description || '')}
      </StyledText>
      <AgendaListContent {...props} />
      <StyledText
        Component={Text}
        fontStyles={footerFontStyles}
        aria-label="agendas footer description"
        color={sidebar.textColor ?? ''}
      >
        {v(footer || '')}
      </StyledText>
      <EditModeOnly>
        <StyledAddAgendaEventButton
          nextPosition={Object.keys(props.items).length + 1}
        />
      </EditModeOnly>
    </Section>
  )
}

function RemoveAgendaListButton() {
  const {removeList, id} = useEditSidebarItem()
  const remove = () => {
    const localizations = [
      `sidebar_agenda_list_${id}_title`,
      `sidebar_agenda_list_${id}_description`,
      `sidebar_agenda_list_${id}_footer`,
    ]
    removeList(localizations)
  }

  return (
    <RemoveButton size="large" showing onClick={remove}>
      Remove Agenda
    </RemoveButton>
  )
}

function AgendaListContent(props: AgendaListProps) {
  const isEdit = useEditMode()
  if (!isEdit) {
    return <AgendaItemList {...props} />
  }
  return <DraggableList {...props} />
}

function DraggableList(props: AgendaListProps) {
  const handleDrag = useHandleDrag(props)

  return (
    <DragDropContext onDragEnd={handleDrag}>
      <Droppable droppableId="drag-and-drop-agendas">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            <>
              <AgendaItemList {...props} />
              {provided.placeholder}
            </>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

function AgendaItemList(props: AgendaListProps) {
  const ids = orderedIdsByPosition(props.items)
  return (
    <>
      {ids.map((id, index) => {
        const agenda = props.items[id]
        return <Agenda agenda={agenda} id={id} key={id} index={index} />
      })}
    </>
  )
}

function useHandleDrag(props: AgendaListProps) {
  const {items} = props
  const {update} = useEditSidebarItem()

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const ids = orderedIdsByPosition(items)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      items: createPositions(ids),
    })
  }
}

const StyledAddAgendaEventButton = styled(AddAgendaButton)`
  margin-bottom: ${(props) => props.theme.spacing[6]}!important;
`

const Text = styled.p<{
  color: string
}>`
  color: ${(props) => props.color};
`
